import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Search } from 'lucide-react';
import { filtrarOpciones } from '../utils/normalizarBusqueda';

/**
 * SearchableSelect — combobox con buscador, sin librerías externas.
 *
 * Pensado para listas largas (p.ej. 264 personas): un <select> nativo
 * no es usable; aquí se escribe para filtrar.
 *
 * props:
 *   value        id seleccionado (string | number | '')
 *   onChange     (id) => void   (id como string; '' = sin selección)
 *   options      [{ id, nombre, alias? }]   (alias: otra forma de buscarla, que no se enseña)
 *   placeholder  texto cuando no hay selección
 *   disabled     bool
 *   loading      bool  (muestra "Cargando…")
 *
 * Accesible básico: navegación ↑/↓/Enter/Esc, cierre al hacer click
 * fuera, resaltado de la opción activa.
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = '— Seleccionar —',
  disabled = false,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  // Posición del panel del dropdown en `fixed` para que escape del
  // overflow del cuerpo del Dialog / Drawer / Card que lo contenga.
  const [panelPos, setPanelPos] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.id) === String(value)) || null,
    [options, value]
  );

  // Búsqueda insensible a acentos (ñ/ç incluidas), por tokens con AND, en cualquier parte del nombre;
  // y por `alias` si la opción lo trae (el literal del INE de un municipio).
  const filtered = useMemo(() => filtrarOpciones(options, query), [options, query]);

  // Cerrar al click fuera. El panel está portal'd a document.body, así
  // que NO es descendiente de rootRef — hay que chequearlo aparte.
  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      const insideRoot = rootRef.current && rootRef.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideRoot && !insidePanel) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Calcular posición del panel a partir del rect del trigger. En
  // `fixed` el origen es el viewport, así escapamos de cualquier
  // overflow:hidden/auto del ancestro (típicamente el cuerpo del
  // Dialog en /inventario/* o el del Drawer en el detalle de activo).
  function computePanelPos() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }

  // Posicionar al abrir + reposicionar en scroll/resize. Usamos scroll
  // en fase de captura (`true`) para detectar también el scroll
  // INTERIOR del Dialog/Drawer/contenedor, no solo el del window.
  useLayoutEffect(() => {
    if (open) computePanelPos();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = () => computePanelPos();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [open]);

  // Al abrir, foco al buscador y resetear índice activo
  useEffect(() => {
    if (open) {
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Mantener visible la opción activa
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx, open]);

  function choose(opt) {
    onChange(opt ? String(opt.id) : '');
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIdx]) choose(filtered[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  const triggerCls =
    'w-full border border-slate-200 rounded-md px-3 py-2 text-sm text-left ' +
    'flex items-center justify-between gap-2 bg-white ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-200 ' +
    (disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer');

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerCls}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? 'text-slate-800 truncate' : 'text-slate-400 truncate'}>
          {loading ? 'Cargando…' : selected ? selected.nombre : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && !disabled && (
            <X
              className="w-4 h-4 text-slate-400 hover:text-slate-600"
              onClick={(e) => {
                e.stopPropagation();
                choose(null);
              }}
            />
          )}
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </span>
      </button>

      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            width: panelPos.width,
            zIndex: 60,
          }}
          className="bg-white border border-slate-200 rounded-md shadow-lg"
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIdx(0);
                }}
                onKeyDown={onKeyDown}
                placeholder="Buscar…"
                className="w-full border border-slate-200 rounded-md pl-8 pr-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
          <ul ref={listRef} className="max-h-60 overflow-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-400">
                Sin resultados
              </li>
            )}
            {filtered.map((o, idx) => (
              <li
                key={o.id}
                data-idx={idx}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
                className={
                  'px-3 py-2 text-sm cursor-pointer truncate ' +
                  (idx === activeIdx
                    ? 'bg-blue-50 text-slate-900'
                    : 'text-slate-700') +
                  (String(o.id) === String(value) ? ' font-medium' : '')
                }
              >
                {o.nombre}
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
